import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { showSuccess, showError } from '../utils/alerts';
import Modal from './Modal';
import { TransactionFormData, CURRENCY_OPTIONS } from '../types/transaction';
import { transactionService } from '../services/transactionService';
import { customerService } from '../services/customerService';
import { itemService } from '../services/itemService';

interface AddSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const validationSchema = yup.object({
  cliente_id: yup.number().min(1, 'Cliente es requerido').required('Cliente es requerido'),
  condiciones_pago: yup.string().default('CONTADO'),
  descuentos: yup.number().min(0, 'Los descuentos deben ser positivos').default(0),
  moneda: yup.string().oneOf(['GTQ', 'USD']).default('GTQ'),
  nota: yup.string().default(''),
  items: yup.array().of(
    yup.object({
      item_id: yup.number().min(1, 'Producto/Servicio es requerido').required('Producto/Servicio es requerido'),
      cantidad: yup.number().min(0.01, 'La cantidad debe ser mayor a 0').required('Cantidad es requerida'),
      precio_unitario: yup.number().min(0, 'El precio debe ser positivo').required('Precio es requerido'),
      descuento: yup.number().min(0, 'El descuento debe ser positivo').default(0)
    })
  ).min(1, 'Debe agregar al menos un producto o servicio')
});

const AddSaleModal: React.FC<AddSaleModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [includeTax, setIncludeTax] = useState(false);
  const [creatingItemIndex, setCreatingItemIndex] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
    watch,
    setValue
  } = useForm<TransactionFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      cliente_id: 0,
      condiciones_pago: 'CONTADO',
      descuentos: 0,
      moneda: 'GTQ',
      nota: '',
      items: [{ item_id: 0, cantidad: 1, precio_unitario: 0, descuento: 0 }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  // Get customers for selection
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getCustomers({ page: 1, limit: 1000 })
      .then(response => response.customers)
  });

  // Get items for selection
  const { data: items = [] } = useQuery({
    queryKey: ['items'],
    queryFn: () => itemService.getItems({ page: 1, limit: 1000 })
      .then(response => response.items)
  });

  const createItemMutation = useMutation({
    mutationFn: itemService.createItem,
    onSuccess: (data) => {
      console.log('✅ Producto/servicio creado exitosamente:', data);
      queryClient.invalidateQueries({ queryKey: ['items'] });
      showSuccess('Producto/servicio creado exitosamente');
      return data;
    },
    onError: (error: any) => {
      console.error('❌ Error al crear producto/servicio:', error);
      const message = error.response?.data?.message || 'Error al crear producto/servicio';
      showError(message);
      throw error;
    }
  });

  const createSaleMutation = useMutation({
    mutationFn: transactionService.createTransaction,
    onSuccess: (data) => {
      console.log('✅ Venta creada exitosamente:', data);
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      showSuccess('Venta creada exitosamente');
      reset();
            setCreatingItemIndex(null);
      onClose();
    },
    onError: (error: any) => {
      console.error('❌ Error detallado en la mutación:', error);
      console.error('❌ Respuesta del servidor:', error.response?.data);
      console.error('❌ Status del error:', error.response?.status);

      const message = error.response?.data?.error?.message ||
                     error.response?.data?.message ||
                     'Error al crear la venta';
      showError(message);
    }
  });

  const watchedItems = watch('items');

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDescuentos = 0;
    let totalImpuestos = 0;

    watchedItems?.forEach((item) => {
      const cantidad = item.cantidad || 0;
      const precio = item.precio_unitario || 0;
      const descuentoItem = item.descuento || 0;
      
      const selectedItem = items.find(i => i.id === item.item_id);
      const tasaImpuesto = includeTax ? (selectedItem?.tax_rate || 0) : 0;

      const totalLinea = cantidad * precio;
      const descuentoLinea = (totalLinea * descuentoItem) / 100;
      const subtotalLinea = totalLinea - descuentoLinea;
      const impuestoLinea = (subtotalLinea * tasaImpuesto) / 100;

      subtotal += subtotalLinea;
      totalDescuentos += descuentoLinea;
      totalImpuestos += impuestoLinea;
    });

    const descuentoGeneral = watch('descuentos') || 0;
    const descuentoGeneralMonto = (subtotal * descuentoGeneral) / 100;
    const total = subtotal - descuentoGeneralMonto + totalImpuestos;

    return {
      subtotal,
      totalDescuentos: totalDescuentos + descuentoGeneralMonto,
      totalImpuestos,
      total
    };
  };

  const totals = calculateTotals();

  const onSubmit = async (data: TransactionFormData) => {
    try {
      console.log('📝 Datos del formulario recibidos:', data);
      
      // Filter out items with invalid item_id (0 or not selected)
      const validItems = (data.items || []).filter(item => item.item_id > 0);
      console.log('✅ Items válidos después del filtro:', validItems);
      
      if (validItems.length === 0) {
        showError('Debe agregar al menos un producto o servicio válido');
        return;
      }

      const saleData = {
        tipo: 'VENTA' as const,
        cliente_id: data.cliente_id,
        condiciones_pago: data.condiciones_pago || 'CONTADO',
        descuentos: data.descuentos || 0,
        moneda: data.moneda,
        nota: data.nota || '',
        items: validItems.map(item => {
          const selectedItem = items.find(i => i.id === item.item_id);
          const tasaImpuesto = includeTax ? (selectedItem?.tax_rate || 0) : 0;

          return {
            item_id: item.item_id,
            descripcion: selectedItem?.name || `Producto/Servicio ID: ${item.item_id}`,
            cantidad: item.cantidad,
            precio_unitario: item.precio_unitario,
            descuento: item.descuento || 0,
            tasa_impuesto: tasaImpuesto
          };
        })
      };

      console.log('📤 Datos que se enviarán al backend:', JSON.stringify(saleData, null, 2));
      
      await createSaleMutation.mutateAsync(saleData);
    } catch (error) {
      console.error('❌ Error en onSubmit:', error);
      // Error is handled in the mutation onError callback
    }
  };

  const handleCustomerChange = (customerId: number) => {
    setValue('cliente_id', customerId);
  };

  const handleItemChange = (index: number, itemId: number) => {
    if (itemId === -1) {
      // Special value for "Create new item"
      setCreatingItemIndex(index);
      setValue(`items.${index}.item_id`, 0); // Reset to default
    } else {
      const item = items.find(i => i.id === itemId);
      if (item) {
        setValue(`items.${index}.precio_unitario`, item.price);
        setCreatingItemIndex(null); // Exit create mode
      }
    }
  };

  const addItem = () => {
    append({ item_id: 0, cantidad: 1, precio_unitario: 0, descuento: 0 });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      if (creatingItemIndex === index) {
        setCreatingItemIndex(null);
      }
    }
  };

  const createNewItem = async (index: number, itemData: any) => {
    try {
      const newItem = await createItemMutation.mutateAsync(itemData);
      setValue(`items.${index}.item_id`, newItem.id);
      setValue(`items.${index}.precio_unitario`, newItem.price);
      setCreatingItemIndex(null);
    } catch (error) {
      // Error is handled in the mutation
    }
  };

  const cancelCreateItem = () => {
    setCreatingItemIndex(null);
  };

  const handleClose = () => {
    reset();
        setIncludeTax(false);
    setCreatingItemIndex(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Nueva Venta" size="xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Customer Selection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="form-label">
              Cliente <span className="text-red-500">*</span>
            </label>
            <select
              {...register('cliente_id', { valueAsNumber: true })}
              onChange={(e) => handleCustomerChange(Number(e.target.value))}
              className="form-select"
            >
              <option value={0}>Seleccionar cliente...</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} - {customer.doc_number}
                </option>
              ))}
            </select>
            {errors.cliente_id && (
              <p className="form-error">{errors.cliente_id.message}</p>
            )}
          </div>

          <div>
            <label className="form-label">Moneda</label>
            <select {...register('moneda')} className="form-select">
              {CURRENCY_OPTIONS.map((currency) => (
                <option key={currency.value} value={currency.value}>
                  {currency.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Payment Terms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Condiciones de Pago</label>
            <input
              type="text"
              {...register('condiciones_pago')}
              className="form-input"
              placeholder="Ej: Contado, Crédito a 30 días"
            />
          </div>

          <div>
            <label className="form-label">Descuento General (%)</label>
            <input
              type="number"
              step="0.01"
              {...register('descuentos', { valueAsNumber: true })}
              className="form-input"
              min="0"
              max="100"
            />
          </div>
        </div>

        {/* Tax Options */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeTaxSale"
              checked={includeTax}
              onChange={(e) => setIncludeTax(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label htmlFor="includeTaxSale" className="ml-2 text-sm font-medium text-gray-900">
              Incluir impuestos (12%)
            </label>
          </div>
          <p className="text-xs text-gray-600 mt-1">
            Al desactivar esta opción, la venta se creará sin impuestos
          </p>
        </div>

        {/* Items Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Productos/Servicios</h3>
            <button
              type="button"
              onClick={addItem}
              className="btn-outline text-sm"
            >
              + Agregar Item
            </button>
          </div>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="p-4 border border-gray-200 rounded-lg">
                {creatingItemIndex === index ? (
                  // Inline item creation form
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900">Crear Nuevo Producto/Servicio</h4>
                      <button
                        type="button"
                        onClick={cancelCreateItem}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="form-label">Tipo</label>
                        <select className="form-select" id={`new-item-type-${index}`}>
                          <option value="PRODUCTO">Producto</option>
                          <option value="SERVICIO">Servicio</option>
                        </select>
                      </div>
                      <div className="lg:col-span-2">
                        <label className="form-label">Nombre</label>
                        <input
                          type="text"
                          className="form-input"
                          id={`new-item-name-${index}`}
                          placeholder="Nombre del producto/servicio"
                        />
                      </div>
                      <div>
                        <label className="form-label">Precio</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-input"
                          id={`new-item-price-${index}`}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const type = (document.getElementById(`new-item-type-${index}`) as HTMLSelectElement)?.value || 'PRODUCTO';
                          const name = (document.getElementById(`new-item-name-${index}`) as HTMLInputElement)?.value || '';
                          const price = parseFloat((document.getElementById(`new-item-price-${index}`) as HTMLInputElement)?.value || '0');

                          if (!name.trim()) {
                            showError('El nombre es requerido');
                            return;
                          }

                          if (price < 0) {
                            showError('El precio debe ser positivo');
                            return;
                          }

                          createNewItem(index, {
                            type,
                            name: name.trim(),
                            code: '',
                            description: '',
                            price,
                            tax_rate: 12,
                            active: true
                          });
                        }}
                        className="btn-primary"
                        disabled={createItemMutation.isPending}
                      >
                        {createItemMutation.isPending ? 'Creando...' : 'Crear y Agregar'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelCreateItem}
                        className="btn-secondary"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  // Normal item selection
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="lg:col-span-2">
                      <label className="form-label">Producto/Servicio</label>
                      <select
                        {...register(`items.${index}.item_id`, { valueAsNumber: true })}
                        onChange={(e) => handleItemChange(index, Number(e.target.value))}
                        className="form-select"
                      >
                        <option value={0}>Seleccionar...</option>
                        <option value={-1} className="text-blue-600 font-medium">+ Crear nuevo producto/servicio</option>
                        {items.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} - {item.type}
                          </option>
                        ))}
                      </select>
                      {errors.items?.[index]?.item_id && (
                        <p className="form-error">{errors.items[index]?.item_id?.message}</p>
                      )}
                    </div>
  
                      <div>
                        <label className="form-label">Cantidad</label>
                        <input
                          type="number"
                          step="0.01"
                          {...register(`items.${index}.cantidad`, { valueAsNumber: true })}
                          className="form-input"
                          min="0"
                        />
                        {errors.items?.[index]?.cantidad && (
                          <p className="form-error">{errors.items[index]?.cantidad?.message}</p>
                        )}
                      </div>
  
                      <div>
                        <label className="form-label">Precio Unitario</label>
                        <input
                          type="number"
                          step="0.01"
                          {...register(`items.${index}.precio_unitario`, { valueAsNumber: true })}
                          className="form-input"
                          min="0"
                        />
                        {errors.items?.[index]?.precio_unitario && (
                          <p className="form-error">{errors.items[index]?.precio_unitario?.message}</p>
                        )}
                      </div>
  
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="form-label">Descuento (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            {...register(`items.${index}.descuento`, { valueAsNumber: true })}
                            className="form-input"
                            min="0"
                            max="100"
                          />
                        </div>
                        {fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="btn-secondary text-red-600 hover:text-red-700 px-3 py-2"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            {errors.items && typeof errors.items === 'object' && 'message' in errors.items && (
              <p className="form-error">{errors.items.message}</p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="form-label">Notas</label>
          <textarea
            {...register('nota')}
            rows={3}
            className="form-textarea"
            placeholder="Notas adicionales para la venta"
          />
        </div>

        {/* Totals Summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Resumen de Venta</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{totals.subtotal.toLocaleString('en-US', { style: 'currency', currency: watch('moneda') || 'GTQ', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between">
              <span>Descuentos:</span>
              <span>-{totals.totalDescuentos.toLocaleString('en-US', { style: 'currency', currency: watch('moneda') || 'GTQ', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between">
              <span>Impuestos:</span>
              <span>{totals.totalImpuestos.toLocaleString('en-US', { style: 'currency', currency: watch('moneda') || 'GTQ', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total a Pagar:</span>
              <span>{totals.total.toLocaleString('en-US', { style: 'currency', currency: watch('moneda') || 'GTQ', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
          
          {/* Sale specific info */}
          <div className="mt-4 p-3 bg-green-100 rounded-lg">
            <p className="text-sm text-green-800 font-medium">
              📋 Esta venta se creará con estado PENDIENTE
            </p>
            <p className="text-xs text-green-600 mt-1">
              Podrá registrar pagos después de crear la venta
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary order-2 sm:order-1"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary order-1 sm:order-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creando...' : 'Crear Venta'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddSaleModal;